/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Content, ModalTextContent, Modal, ModalContent, TitleGroup } from './styles'
import ContentProps from './Content'
import { toast } from 'react-toastify'
import Calendar from 'react-calendar'
import { Value } from 'react-calendar/dist/shared/types'
import 'react-calendar/dist/Calendar.css'
import { useNavigate } from 'react-router-dom'
import api from '../../services/axios'
import HeaderPainel from '../../components/Common/HeaderPainel'
import { IoCalendarSharp } from 'react-icons/io5'
import { FaFileExcel } from 'react-icons/fa'
import { Skeleton } from '../../components/Skeletron'
import { useAuth } from '../../context/AuthContext'
import { carregarDistribuidores } from '../../services/dists'
import { useCustomTheme } from '../../context/Theme/ThemeContextData'

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

const isBeforeDay = (a: Date, b: Date) => startOfDay(a).getTime() < startOfDay(b).getTime()

const getYesterday = (ref = new Date()) => {
  const y = new Date(ref)
  y.setDate(ref.getDate() - 1)
  return startOfDay(y)
}

export type StatusFilter = 'all' | 'justificado' | 'atraso' | 'enviado' | 'domingo'

function RegularView() {
  const { user } = useAuth()
  const { theme } = useCustomTheme()
  const statusTextColor = theme === 'light' ? '#1e293b' : '#ffffff'
  const [modal, setModal] = useState(false)
  const [activeFilter, setActiveFilter] = useState<StatusFilter>('all')
  const [role, setRole] = useState('')
  const [obs, setObs] = useState('')
  const [arquivo, setArquivo] = useState('')
  const [value, onChange] = useState<Value>(getYesterday())
  const [showCalendar, setShowCalendar] = useState(false)
  const navigate = useNavigate()
  const [file, setFiles] = useState<any[]>([])
  const [userRole, setUserRole] = useState<string | null>(null)
  const [distribuidores, setDistribuidores] = useState<{ id: string; name: string }[]>([])
  const [selectedDist, setSelectedDist] = useState<string>('')
  const [loading, setLoading] = useState(true)

  const today = startOfDay(new Date())

  useEffect(() => {
    fetchDist()
  }, [])

  const toggleFilter = (filter: StatusFilter) => {
    setActiveFilter((prev) => (prev === filter ? 'all' : filter))
  }

  const fetchDist = async () => {
    try {
      setUserRole(user?.role ?? null)

      // /dist e /files já vêm recortados pelo token. O administrador antes
      // não carregava arquivo nenhum aqui — só os outros dois papéis é que
      // buscavam — e agora todos recebem o mesmo conjunto.
      const [distList, resFiles] = await Promise.all([
        carregarDistribuidores(),
        api.get('/files'),
      ])

      setDistribuidores(distList)
      setFiles(Array.isArray(resFiles.data) ? resFiles.data : [])

      if (distList.length > 0) {
        setSelectedDist(distList[0].id)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  async function handleSingIn() {
    try {
      if (selectedDist.length === 0) {
        toast.error("Preencha o campo 'Distribuidor'")
        return
      }

      if (role === '') {
        toast.error("Preencha o campo 'MOTIVO'")
        return
      }

      if (!isBeforeDay(value as Date, today)) {
        toast.error('Somente arquivos com data anterior a hoje podem ser enviados.')
        return
      }

      if (role === 'Outros' && obs === '') {
        toast.error("Preencha o campo 'OBSERVAÇÂO'")
        return
      }

      await api.post('j/post', {
        dist_id: selectedDist,
        dt_referencia: value,
        tipo: role,
        justificativa: obs,
        nm_arquivo: arquivo,
      })

      toast.success('Justificativa enviada', {
        position: 'top-right',
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
      setModal(false)
    } catch (error) {
      toast.error('Já existe justificativa para esse dia')
    }
  }

  const tileDisabled = ({ date, view }: { date: Date; view: string }) => {
    if (view === 'month') {
      // retorna true para desabilitar quando date >= today
      return !isBeforeDay(date, today)
    }
    return false
  }

  const exportRef = useRef<(() => void) | null>(null)

  const handleRegisterExport = useCallback((exportFn: () => void) => {
    exportRef.current = exportFn
  }, [])

  const handleExportExcel = () => {
    if (exportRef.current) {
      exportRef.current()
    } else {
      toast.info('Carregando dados da tela, aguarde um instante...')
    }
  }

  const memorizedContentProps = useMemo(() => {
    return (
      <ContentProps
        activeFilter={activeFilter}
        onRegisterExport={handleRegisterExport}
      />
    )
  }, [activeFilter, handleRegisterExport])

  if (loading) return <Skeleton variant='table' />

  return (
    <Content>
      <TitleGroup>
        <h2>Regularidades</h2>
        <p>Monitoramento de regularidades de envios</p>
      </TitleGroup>
      <HeaderPainel />

      <div className='action'>
        <div className='action-double'>
          <div
            onClick={() => toggleFilter('justificado')}
            style={{
              display: 'flex',
              gap: 10,
              cursor: 'pointer',
              opacity: activeFilter !== 'all' && activeFilter !== 'justificado' ? 0.3 : 1,
              transition: 'opacity 0.3s',
            }}
          >
            <span style={{ background: '#ff8400', width: 16, height: 16, borderRadius: 20 }}></span>
            <p
              style={{
                color: statusTextColor,
                fontWeight: activeFilter === 'justificado' ? 'bold' : 500,
                fontStyle: 'normal',
              }}
            >
              Dias Justificados
            </p>
          </div>
          <div
            onClick={() => toggleFilter('atraso')}
            style={{
              display: 'flex',

              gap: 10,
              cursor: 'pointer',
              opacity: activeFilter !== 'all' && activeFilter !== 'atraso' ? 0.3 : 1,
              transition: 'opacity 0.3s',
            }}
          >
            <span style={{ background: '#d60606', width: 16, height: 16, borderRadius: 20 }}></span>
            <p
              style={{
                color: statusTextColor,
                fontWeight: activeFilter === 'atraso' ? 'bold' : 500,
                fontStyle: 'normal',
              }}
            >
              Dias em Atraso
            </p>
          </div>
          <div
            onClick={() => toggleFilter('enviado')}
            style={{
              display: 'flex',

              gap: 10,
              cursor: 'pointer',
              opacity: activeFilter !== 'all' && activeFilter !== 'enviado' ? 0.3 : 1,
              transition: 'opacity 0.3s',
            }}
          >
            <span style={{ background: '#45ce01', width: 16, height: 16, borderRadius: 20 }}></span>
            <p
              style={{
                color: statusTextColor,
                fontWeight: activeFilter === 'enviado' ? 'bold' : 500,
                fontStyle: 'normal',
              }}
            >
              Dias Enviados
            </p>
          </div>
          <div
            onClick={() => toggleFilter('domingo')}
            style={{
              display: 'flex',

              gap: 10,
              cursor: 'pointer',
              opacity: activeFilter !== 'all' && activeFilter !== 'domingo' ? 0.3 : 1,
              transition: 'opacity 0.3s',
            }}
          >
            <span style={{ background: '#a7a7a7', width: 16, height: 16, borderRadius: 20 }} />
            <p
              style={{
                color: statusTextColor,
                fontWeight: activeFilter === 'domingo' ? 'bold' : 500,
                fontStyle: 'normal',
              }}
            >
              Domingo
            </p>
          </div>
        </div>
        <div className='action-buttons'>
          <button
            className='export-excel'
            type='button'
            onClick={handleExportExcel}
            title='Exportar relatório de regularidade em Excel'
          >
            <FaFileExcel size={16} />
            Exportar Excel
          </button>
          {userRole !== 'visualizador' && userRole !== 'conferente' && (
            <button className='justify' type='button' onClick={() => setModal(true)}>
              JUSTIFICAR
            </button>
          )}
        </div>
      </div>

      {memorizedContentProps}
      {modal === true && (
        <Modal>
          <ModalContent>
            <ModalTextContent>
              <div className='header'>
                <p>
                  Justificativa para ausência de envio de dia:{' '}
                  {value instanceof Date ? value.getDate() : ''}
                </p>
              </div>
              <div className='content-modal'>
                <div className='context'>
                  <div className='dist'>
                    <p>Distribuidor:</p>
                    <div
                      style={{ display: 'flex', flexDirection: 'column', gap: 15, width: '100%' }}
                    >
                      <select
                        className='select'
                        value={selectedDist}
                        onChange={(e) => setSelectedDist(e.target.value)}
                      >
                        <option value=''>Selecione o Distribuidor</option>
                        {distribuidores.map((d: any) => {
                          const id = d.id || d._id
                          const name = d.fantasy_name || d.name || d.nome || d.razao_social || 'Distribuidor'
                          return (
                            <option key={id} value={id}>
                              {name}
                            </option>
                          )
                        })}
                      </select>
                    </div>
                  </div>
                  <div className='about'>
                    <p>Motivo:</p>
                    <select required value={role} onChange={(e) => setRole(e.target.value)}>
                      <option value=''>Selecione o motivo</option>
                      <option value='Feriado'>Feriado</option>
                      <option value='Domingo'>Domingo</option>
                      <option value='Outros'>Outros</option>
                    </select>
                  </div>

                  {role === 'Outros' && (
                    <>
                      <div className='obs'>
                        <p>OBSERVAÇÂO:</p>
                        <textarea value={obs} onChange={(e) => setObs(e.target.value)}></textarea>
                      </div>
                    </>
                  )}

                  <button
                    type='button'
                    className='icon-calendar'
                    onClick={() => setShowCalendar(!showCalendar)}
                  >
                    <IoCalendarSharp color='#ffffff' size={24} />

                    <p style={{ color: '#ffffff' }}>
                      Dia <br />
                      {value instanceof Date ? value.getDate() : ''}
                    </p>
                  </button>

                  {showCalendar === true ? (
                    <Calendar
                      onChange={(d: Value) => onChange(d)}
                      value={value}
                      onClickDay={() => setShowCalendar(false)}
                      className='react-calendar'
                      tileDisabled={tileDisabled}
                    />
                  ) : null}

                  <div className='button'>
                    <button type='button' onClick={() => setModal(false)}>
                      Cancelar
                    </button>
                    <button type='button' onClick={handleSingIn}>
                      Justificar
                    </button>
                  </div>
                </div>
              </div>
            </ModalTextContent>
          </ModalContent>
        </Modal>
      )}
    </Content>
  )
}

export default RegularView
